import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { entryKeys } from '@lib/keys'
import { getEntriesByTimeCardId, searchEntries, updateTimeCardEntryOrder } from '@lib/api'
import { useMemo } from 'react'
import { DateTime } from 'luxon'

interface Options {
  start: DateTime
  end: DateTime
  userId: number
}

export const useEntries = (options: Options) => {
  return useQuery(entryKeys.search(options.userId, options.start, options.end), () =>
    searchEntries(options.userId, options.start, options.end),
  )
}

/**
 * useEntriesByTimeCard and useFetchEntriesByTimeCard (below) differ in how
 * they derive entries.  The former searches, and the latter fetches by
 * time card id. The latter is used within an admin context, while the former
 * is used within the time logging context.
 */
export const useEntriesByTimeCard = (timeCardId: number, userId: number, start: DateTime, end: DateTime) => {
  const entriesQuery = useEntries({ userId, start, end })

  return useMemo(() => {
    if (entriesQuery.data === undefined) return []

    return entriesQuery.data.filter((entry) => entry.timeCard.id === timeCardId)
  }, [entriesQuery.data, timeCardId])
}

export const useFetchEntriesByTimeCard = (timeCardId = 0) => {
  return useQuery(
    entryKeys.byTimeCard(timeCardId),
    () => getEntriesByTimeCardId(timeCardId),
    { enabled: timeCardId !== 0 },
  )
}

export const useUpdateTimeCardEntryOrder = (timeCardId: number) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (entryIds: number[]) => {
      return updateTimeCardEntryOrder(timeCardId, entryIds)
    },
    onSuccess: () => {
      // Invalidate the query to refetch entries with updated sortPosition
      void queryClient.invalidateQueries({
        queryKey: entryKeys.byTimeCard(timeCardId),
      })
    },
  })
}
